import { trackApiClient } from '@/clients/TrackApiClient';
import { IArtist } from '@/data/models/artist';
import { IPublisher, ITrack, IWriterComposer } from '@/data/models/track';
import {
  ErrorCode,
  ErrorType,
  IValidationResults,
  ValidationLevel,
} from '@/data/models/validation';
import { ILoadAlbum, loadAlbum } from '@/state/actions/albumAction';
import store from '@/state/store';
import { formatSideLetter } from '@/utils/helper';
import {
  findIsrcMetadataConflictMismatchForTrackField,
  formatIsrcMetadataConflictMismatchMessage,
  formatIsrcMetadataConflictValidationMessage,
} from '@/utils/isrcMetadataConflictFormatting';
import _, { cloneDeep } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IAlbum } from '@/data/models/album';
import { IChangeRequest } from '@/data/models/changeRequest';
import { changeRequestApiClient } from '@/clients/ChangeRequestAPIClient';
import { isValidationError } from '@/utils/validationErrors';
import { getSaveErrorMessage } from '@/utils/saveErrorMessages';
import { logger } from '@/utils/Logger';

interface UseTrackEditViewProps {
  togglePopUp: () => void;
  trackObj: ITrack;
  reloadTracks: () => void;
  errorFlag: boolean;
  errors: IValidationResults[];
  catalogUpc?: string | null;
  readonly: boolean;
  loadPreviousTrack: () => void;
  loadNextTrack: () => void;
  changeRequest?: IChangeRequest;
}

export const useTrackEditView = ({
  togglePopUp,
  trackObj,
  reloadTracks,
  errorFlag,
  errors,
  catalogUpc,
  readonly,
  loadPreviousTrack,
  loadNextTrack,
  changeRequest,
}: UseTrackEditViewProps) => {
  const { t } = useTranslation();
  const initialTrack = cloneDeep(trackObj);
  const [track, setTrack] = useState<ITrack>(initialTrack);
  const [showPopUp, setShowPopUp] = useState(true);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [errorBanner, setErrorBanner] = useState(false);
  const [trackErrors, setTrackErrors] = useState<IValidationResults[]>([]);
  const [trackIssues, setTrackIssues] = useState<IValidationResults[]>([]);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const [showCancelChangesModal, setShowCancelChangesModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTrackValid, setIsTrackValid] = useState(true);

  useEffect(() => {
    errorMessageHandling(errorFlag);
  }, [errorFlag, errors, track?.id]);

  useEffect(() => {
    setTrack(cloneDeep(trackObj));
  }, [trackObj]);

  const errorMessageHandling = (errorFlag: boolean) => {
    const hasValidationErrors =
      (errorFlag === true || errors.length > 0) && errors !== null;

    if (!hasValidationErrors) {
      setTrackErrors([]);
      setTrackIssues([]);
      setErrorMessages([]);
      setErrorBanner(false);
      return;
    }

    const currentTrackIssues = errors.filter(
      (va) =>
        va.validationLevel === ValidationLevel.Track && va.trackId === track.id
    );
    const currentTrackErrors = currentTrackIssues.filter(isValidationError);
    setTrackIssues(currentTrackIssues);
    // Field styling uses both validation errors and warnings.
    setTrackErrors(currentTrackIssues);

    const errorMessages: string[] = [];

    const trackInfoMissingOrInvalidErrors: IValidationResults[] =
      currentTrackErrors.filter(
        (va) =>
          va.errorCode === ErrorCode.TrackInfoMissingOrInvalid ||
          va.errorType === ErrorType.EmptyList
      );

    if (trackInfoMissingOrInvalidErrors.length > 0)
      errorMessages.push(
        t('validationAlert.trackEditMode.trackInfoMissingOrInvalidErrorMessage')
      );

    setErrorMessages(errorMessages);
    setErrorBanner(currentTrackIssues.length > 0);
  };

  const closePopUp = () => {
    setShowPopUp(false);
    togglePopUp();
  };

  const onCancel = useCallback(() => {
    if (_.isEqual(track, trackObj)) {
      closePopUp();
    } else {
      setShowCancelChangesModal(true);
    }
  }, [track, trackObj, togglePopUp]);

  const handleInput = (field: keyof ITrack, value: ITrack[keyof ITrack]) => {
    setTrack({ ...track, [field]: value });
  };

  const handleSubmit = async (closeModal: boolean): Promise<boolean> => {
    setIsSubmitting(true);
    setSaveErrorMessage(null);

    try {
      if (
        !readonly &&
        (track.id === 0 || JSON.stringify(track) !== JSON.stringify(trackObj))
      ) {
        let result: Promise<any>;

        if (changeRequest) {
          const updatedChangeRequest: IAlbum = _.cloneDeep(
            changeRequest.changeRequestAlbum
          );
          updatedChangeRequest.tracks = _.map(
            updatedChangeRequest.tracks,
            (t) => {
              return t.id === track.id ? { ...track } : t;
            }
          );
          result = changeRequestApiClient.updateChangeRequest(
            changeRequest.albumId,
            changeRequest.id!,
            { ...changeRequest, changeRequestAlbum: updatedChangeRequest }
          );
        } else {
          if (track.id === 0) {
            result = trackApiClient.createTrack(track);
          } else {
            result = trackApiClient.updateTrack(track.id!, track);
          }
        }
        const res = await result;
        if (res) {
          const loadAlbumValues: ILoadAlbum = {
            isJustCreated: false,
          };
          await store.dispatch(loadAlbum(loadAlbumValues));
          reloadTracks();
        }
      }

      if (closeModal) {
        setShowPopUp(false);
        togglePopUp();
      }
      return true;
    } catch (error: any) {
      logger.error(error);
      setSaveErrorMessage(getSaveErrorMessage(error, !!changeRequest));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextTrack = async () => {
    if (!readonly) {
      const saveSucceeded = await handleSubmit(false);
      if (!saveSucceeded) return;
    }
    loadNextTrack();
  };

  const previousTrack = async () => {
    if (!readonly) {
      const saveSucceeded = await handleSubmit(false);
      if (!saveSucceeded) return;
    }
    loadPreviousTrack();
  };

  const handleDisplayArtist = (artistData: IArtist[]): string => {
    let displayArtist: string = '';
    let displayMainArtist: string = '';
    let displayFeaturedArtist: string = '';
    const mainArtists = artistData.filter(
      ({ performerRole }) => performerRole === 'Main'
    );
    const featuredArtists = artistData.filter(
      ({ performerRole }) => performerRole === 'Featured'
    );

    mainArtists.forEach((item) => {
      if (displayMainArtist === '') {
        displayMainArtist = item.name;
      } else {
        displayMainArtist = displayMainArtist + ', ' + item.name;
      }
    });

    featuredArtists.forEach((item) => {
      if (displayFeaturedArtist === '') {
        displayFeaturedArtist = ' feat. ' + item.name;
      } else {
        displayFeaturedArtist = displayFeaturedArtist + ', ' + item.name;
      }
    });

    displayArtist = displayMainArtist + displayFeaturedArtist;
    return displayArtist;
  };

  const handleArtist = (artistData: IArtist[]) => {
    if (
      artistData.length === 1 &&
      artistData[0].name === '' &&
      artistData[0].performerRole === 'Please Select'
    ) {
      artistData = [];
    }
    const artists = artistData.map((item: IArtist) => item);
    const displayArtist = handleDisplayArtist(artists);
    setTrack({ ...track, artists: artists, displayArtist: displayArtist });
  };

  const handleWriterComposer = (writerComposerData: IWriterComposer[]) => {
    if (
      writerComposerData.length === 1 &&
      writerComposerData[0].name === '' &&
      writerComposerData[0].role === 'Please Select'
    ) {
      writerComposerData = [];
    }
    const writerComposer = writerComposerData.map(
      (item: IWriterComposer) => item
    );
    setTrack({ ...track, writersComposers: writerComposer });
  };

  const handlePublisher = (publisherData: IPublisher[]) => {
    if (
      publisherData.length === 1 &&
      publisherData[0].name === '' &&
      publisherData[0].performing_Rights_Organization === ''
    ) {
      publisherData = [];
    }
    const publishers = publisherData.map((item: IPublisher) => item);
    setTrack({ ...track, publishers: publishers });
  };

  const handleDuration = (
    newDuration: number | undefined,
    isNewDurationValid: boolean
  ) => {
    handleInput('duration', newDuration);
    setIsTrackValid(isNewDurationValid);
  };

  const fieldHasErrors = (fieldName: string) => {
    return (
      !!trackErrors.find((va) => va.fieldName === fieldName) ||
      !!findIsrcMetadataConflictMismatchForTrackField(
        trackErrors,
        track.id,
        fieldName
      )
    );
  };

  const fieldValidationState = (fieldName: string) => {
    const directIssue = trackErrors.find((va) => va.fieldName === fieldName);
    if (directIssue) {
      return directIssue.isValid;
    }

    return findIsrcMetadataConflictMismatchForTrackField(
      trackErrors,
      track.id,
      fieldName
    )
      ? 'Error'
      : undefined;
  };

  const findErrorMessage = (field: string): string => {
    const validationError = trackErrors.find(
      (va) =>
        va.fieldName === field &&
        va.validationLevel === ValidationLevel.Track &&
        va.trackId === track.id
    );
    const errorType = validationError?.errorType;
    let errorMessage: string = '';
    if (errorType) {
      if (errorType === ErrorType.MetadataConflict && validationError) {
        return formatIsrcMetadataConflictValidationMessage(
          validationError,
          catalogUpc
        );
      }

      const translationKey =
        'validationAlert.trackEditMode.' +
        field +
        '.' +
        errorType +
        '.ErrorMessage';
      errorMessage = t(translationKey);

      if (errorMessage === translationKey) {
        return validationError?.validationMessage ?? '';
      }
    }

    const metadataConflictMismatch =
      findIsrcMetadataConflictMismatchForTrackField(
        trackErrors,
        track.id,
        field
      );
    if (metadataConflictMismatch) {
      return formatIsrcMetadataConflictMismatchMessage(
        metadataConflictMismatch.details,
        metadataConflictMismatch.mismatch,
        catalogUpc
      );
    }

    return errorMessage;
  };

  const formatTrackLabel = (
    usesSides: boolean,
    volumeNumber: number
  ): string => {
    let discOrSide;
    let volumeNumberOrLetter;
    if (usesSides) {
      discOrSide = t('product.side');
      volumeNumberOrLetter = formatSideLetter(volumeNumber);
    } else {
      discOrSide = t('product.disc');
      volumeNumberOrLetter = `${volumeNumber}`;
    }

    return t('track.volumeNumberLabel', { discOrSide, volumeNumberOrLetter });
  };

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [onCancel]);

  return {
    track,
    showPopUp,
    errorMessages,
    errorBanner,
    trackIssues,
    saveErrorMessage,
    setSaveErrorMessage,
    showCancelChangesModal,
    setShowCancelChangesModal,
    isSubmitting,
    isTrackValid,
    onCancel,
    closePopUp,
    handleInput,
    nextTrack,
    previousTrack,
    handleSubmit,
    handleArtist,
    handleWriterComposer,
    handlePublisher,
    handleDuration,
    fieldHasErrors,
    fieldValidationState,
    findErrorMessage,
    formatTrackLabel,
  };
};
